/* -------------------------------------------------------------------------- */
/*                          useResourceDragAndDrop                            */
/* -------------------------------------------------------------------------- */

import { useCallback, useEffect, useRef, useState } from 'react';
import { MRT_Row } from 'mantine-react-table';
import {
  ResourceRowFragmentFragment,
  ResourceType,
} from '@app-types/blackroom-api';

/* -------------------------------------------------------------------------- */
/*                                   Types                                    */
/* -------------------------------------------------------------------------- */

/** Row type that can be partial (as returned by table.getState()) */
type PartialRow = Partial<MRT_Row<ResourceRowFragmentFragment>> | null;

interface UseResourceDragAndDropOptions {
  tableContainerRef: React.RefObject<HTMLDivElement | null>;
  moveResources: (
    target: ResourceRowFragmentFragment,
    dragged: ResourceRowFragmentFragment,
  ) => void | Promise<unknown>;
  moveAndRankResources: (
    target: ResourceRowFragmentFragment,
    dragged: ResourceRowFragmentFragment,
  ) => void | Promise<unknown>;
}

interface UseResourceDragAndDropResult {
  /** Whether a specific row is the current drop target (hovered long enough) */
  isDropTarget: (rowId: string) => boolean;
  /** Handler for drag events (auto-scroll + folder hover tracking) */
  handleDragAutoScroll: (event: React.DragEvent) => void;
  /** Handler for drag exit events */
  handleDragExit: () => void;
  /** Handler for drag end events - performs the actual move */
  handleDragEnd: (
    hoveredRow: PartialRow,
    draggingRow: PartialRow,
  ) => Promise<void>;
  /** Handler for during drag events - tracks folder hover time */
  handleDragOver: (hoveredRow: PartialRow, draggingRow: PartialRow) => void;
}

/* -------------------------------------------------------------------------- */
/*                                 Constants                                  */
/* -------------------------------------------------------------------------- */

/** How close to edge (in pixels) before scrolling starts */
const EDGE_THRESHOLD = 80;
/** Maximum scroll speed in pixels per frame */
const MAX_SCROLL_SPEED = 15;
/** Time (ms) to hover over a folder before "move into folder" mode activates */
const FOLDER_HOVER_THRESHOLD = 1200;

/* -------------------------------------------------------------------------- */
/*                                    Hook                                    */
/* -------------------------------------------------------------------------- */

export function useResourceDragAndDrop({
  tableContainerRef,
  moveResources,
  moveAndRankResources,
}: UseResourceDragAndDropOptions): UseResourceDragAndDropResult {
  /* ********************************** REFS ********************************** */

  const autoScrollAnimationRef = useRef<number | null>(null);
  const isDraggingRef = useRef<boolean>(false);

  // Track which folder we're hovering over and when we started
  const hoveredFolderIdRef = useRef<string | null>(null);
  const folderHoverStartTimeRef = useRef<number | null>(null);

  /* ********************************** STATE ********************************* */

  // Visual feedback: which folder has reached the "move into" threshold
  const [activeFolderDropTarget, setActiveFolderDropTarget] = useState<
    string | null
  >(null);

  // This ref tracks what the user has ACTUALLY SEEN (after DOM paint)
  // We use double-requestAnimationFrame to ensure the browser has actually painted
  const visualFeedbackPaintedRef = useRef<string | null>(null);
  // Track WHEN the visual was painted so we can ensure minimum visible time
  const visualFeedbackPaintedAtRef = useRef<number | null>(null);

  // Minimum time (ms) the visual must be visible before allowing "move into folder"
  // This ensures the user has time to perceive the visual feedback
  const MINIMUM_VISIBLE_TIME = 100;

  // Update the painted ref AFTER the browser has actually painted
  useEffect(() => {
    const rafId = requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        visualFeedbackPaintedRef.current = activeFolderDropTarget;
        visualFeedbackPaintedAtRef.current = activeFolderDropTarget
          ? Date.now()
          : null;
      });
    });

    return () => cancelAnimationFrame(rafId);
  }, [activeFolderDropTarget]);

  /* ************************** FOLDER HOVER TRACKING ************************* */

  // Check periodically if we've crossed the threshold for visual feedback
  useEffect(() => {
    const checkThreshold = setInterval(() => {
      if (
        hoveredFolderIdRef.current &&
        folderHoverStartTimeRef.current !== null
      ) {
        const hoverDuration = Date.now() - folderHoverStartTimeRef.current;
        if (hoverDuration >= FOLDER_HOVER_THRESHOLD) {
          setActiveFolderDropTarget(hoveredFolderIdRef.current);
        }
      }
    }, 100);

    return () => clearInterval(checkThreshold);
  }, []);

  // Cleanup auto-scroll on unmount
  useEffect(() => {
    return () => {
      if (autoScrollAnimationRef.current !== null) {
        cancelAnimationFrame(autoScrollAnimationRef.current);
      }
    };
  }, []);

  /**
   * Start tracking hover time for a folder
   */
  const startFolderHoverTracking = useCallback(
    (folderId: string) => {
      // Only reset the timer if we're hovering over a different folder
      if (hoveredFolderIdRef.current !== folderId) {
        hoveredFolderIdRef.current = folderId;
        folderHoverStartTimeRef.current = Date.now();
        // Clear visual feedback since we're on a new folder
        setActiveFolderDropTarget(null);
      } else if (folderHoverStartTimeRef.current !== null) {
        // Same folder - check threshold immediately for responsive visual feedback
        const hoverDuration = Date.now() - folderHoverStartTimeRef.current;
        if (
          hoverDuration >= FOLDER_HOVER_THRESHOLD &&
          activeFolderDropTarget !== folderId
        ) {
          setActiveFolderDropTarget(folderId);
        }
      }
    },
    [activeFolderDropTarget],
  );

  /**
   * Clear folder hover tracking
   */
  const clearFolderHoverTracking = useCallback(() => {
    hoveredFolderIdRef.current = null;
    folderHoverStartTimeRef.current = null;
    setActiveFolderDropTarget(null);
  }, []);

  /* ***************************** AUTO-SCROLL ******************************* */

  const stopAutoScroll = useCallback(() => {
    if (autoScrollAnimationRef.current !== null) {
      cancelAnimationFrame(autoScrollAnimationRef.current);
      autoScrollAnimationRef.current = null;
    }
    isDraggingRef.current = false;
  }, []);

  const handleDragAutoScroll = useCallback(
    (event: React.DragEvent): void => {
      const container = tableContainerRef.current;
      if (!container) return;

      isDraggingRef.current = true;
      const containerRect = container.getBoundingClientRect();
      const mouseY = event.clientY;

      const distanceFromTop = mouseY - containerRect.top;
      const distanceFromBottom = containerRect.bottom - mouseY;

      const isNearEdge =
        (distanceFromTop < EDGE_THRESHOLD && distanceFromTop > 0) ||
        (distanceFromBottom < EDGE_THRESHOLD && distanceFromBottom > 0);

      const performAutoScroll = () => {
        if (!isDraggingRef.current || !tableContainerRef.current) {
          stopAutoScroll();
          return;
        }

        let scrollDelta = 0;

        if (distanceFromTop < EDGE_THRESHOLD && distanceFromTop > 0) {
          // Scroll up - speed increases as we get closer to the edge
          const intensity = 1 - distanceFromTop / EDGE_THRESHOLD;
          scrollDelta = -MAX_SCROLL_SPEED * intensity;
        } else if (
          distanceFromBottom < EDGE_THRESHOLD &&
          distanceFromBottom > 0
        ) {
          // Scroll down - speed increases as we get closer to the edge
          const intensity = 1 - distanceFromBottom / EDGE_THRESHOLD;
          scrollDelta = MAX_SCROLL_SPEED * intensity;
        }

        if (scrollDelta !== 0) {
          tableContainerRef.current.scrollTop += scrollDelta;
          // Continue scrolling while dragging near edge
          autoScrollAnimationRef.current =
            requestAnimationFrame(performAutoScroll);
        } else {
          autoScrollAnimationRef.current = null;
        }
      };

      // Cancel any existing animation and start a new one if needed
      if (autoScrollAnimationRef.current !== null) {
        cancelAnimationFrame(autoScrollAnimationRef.current);
        autoScrollAnimationRef.current = null;
      }

      if (isNearEdge) {
        autoScrollAnimationRef.current =
          requestAnimationFrame(performAutoScroll);
      }
    },
    [tableContainerRef, stopAutoScroll],
  );

  /* *************************** DRAG EVENT HANDLERS ************************** */

  const handleDragOver = useCallback(
    (hoveredRow: PartialRow, draggingRow: PartialRow) => {
      const hoveredRowId = hoveredRow?.original?.id;
      const draggingRowId = draggingRow?.original?.id;

      if (hoveredRowId && hoveredRowId !== draggingRowId) {
        // Check if hovering over a folder
        if (hoveredRow?.original?.type === ResourceType.Folder) {
          startFolderHoverTracking(hoveredRowId);
        } else {
          // Hovering over a file, not a folder - clear tracking
          clearFolderHoverTracking();
        }
      } else {
        // Not hovering over a valid target
        clearFolderHoverTracking();
      }
    },
    [startFolderHoverTracking, clearFolderHoverTracking],
  );

  const handleDragExit = useCallback(() => {
    stopAutoScroll();
    clearFolderHoverTracking();
  }, [stopAutoScroll, clearFolderHoverTracking]);

  const handleDragEnd = useCallback(
    async (hoveredRow: PartialRow, draggingRow: PartialRow) => {
      stopAutoScroll();
      const targetResource = hoveredRow?.original;
      const draggedResource = draggingRow?.original;

      if (targetResource?.id && draggedResource?.id) {
        const now = Date.now();
        const hoverDuration = folderHoverStartTimeRef.current
          ? now - folderHoverStartTimeRef.current
          : null;

        // Check if hover threshold has passed
        const hasHoveredLongEnough =
          hoveredFolderIdRef.current === targetResource.id &&
          targetResource.type === ResourceType.Folder &&
          folderHoverStartTimeRef.current !== null &&
          hoverDuration !== null &&
          hoverDuration >= FOLDER_HOVER_THRESHOLD;

        // Calculate how long the visual has been visible
        const visualVisibleDuration = visualFeedbackPaintedAtRef.current
          ? now - visualFeedbackPaintedAtRef.current
          : 0;

        // Only move into folder if ALL of these are true:
        // 1. The hover threshold has passed
        // 2. The visual feedback has been painted
        // 3. The visual has been visible for at least MINIMUM_VISIBLE_TIME
        const shouldMove =
          hasHoveredLongEnough &&
          visualFeedbackPaintedRef.current === targetResource.id &&
          visualVisibleDuration >= MINIMUM_VISIBLE_TIME;

        if (shouldMove) {
          await moveResources(targetResource, draggedResource);
        } else {
          await moveAndRankResources(targetResource, draggedResource);
        }
      }

      // Clear tracking
      clearFolderHoverTracking();
    },
    [
      stopAutoScroll,
      clearFolderHoverTracking,
      moveResources,
      moveAndRankResources,
    ],
  );

  /* ******************************** HELPERS ********************************* */

  const isDropTarget = useCallback(
    (rowId: string) => {
      return activeFolderDropTarget === rowId;
    },
    [activeFolderDropTarget],
  );

  /* ********************************* RETURN ********************************* */

  return {
    isDropTarget,
    handleDragAutoScroll,
    handleDragExit,
    handleDragEnd,
    handleDragOver,
  };
}

export default useResourceDragAndDrop;
